import React, { useState, useEffect, useCallback } from 'react';
import { useAuthStore } from '../../store/authStore';
import { FirebaseService } from '../../services/firebaseService';
import { Wifi, WifiOff, AlertTriangle, CheckCircle, RefreshCw } from 'lucide-react';
import { COLORS } from '../../styles/theme';

export const FirebaseStatus: React.FC<{ compact?: boolean }> = ({ compact = false }) => {
  const { user, userData } = useAuthStore();
  const [connectionStatus, setConnectionStatus] = useState<'checking' | 'connected' | 'disconnected' | 'error'>('checking');
  const [lastCheck, setLastCheck] = useState<Date | null>(null);

  const checkConnection = useCallback(async () => {
    setConnectionStatus('checking');
    try {
      // Verificar que hay usuario autenticado antes de hacer la consulta
      if (!user || !userData) {
        setConnectionStatus('disconnected');
        setLastCheck(new Date());
        return;
      }

      // Intentar una operación simple para verificar la conexión
      await FirebaseService.getCollection('users');
      setConnectionStatus('connected');
      setLastCheck(new Date());
    } catch (error) {
      console.error('Error checking Firebase connection:', error);
      
      // Si es error de autenticación, marcar como desconectado
      if (error instanceof Error && error.message.includes('Usuario no autenticado')) {
        setConnectionStatus('disconnected');
      } else {
        setConnectionStatus('error');
      }
      
      setLastCheck(new Date());
    }
  }, [user, userData]);

  useEffect(() => {
    // Solo verificar conexión si hay usuario autenticado
    if (user && userData) {
      checkConnection();
      
      // Verificar conexión cada 30 segundos
      const interval = setInterval(checkConnection, 30000);
      
      return () => clearInterval(interval);
    }
  }, [user, userData, checkConnection]);

  const getStatusIcon = () => {
    switch (connectionStatus) {
      case 'connected':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'disconnected':
        return <WifiOff className="h-4 w-4 text-red-500" />;
      case 'error':
        return <AlertTriangle className="h-4 w-4 text-yellow-500" />;
      default:
        return <Wifi className="h-4 w-4 text-gray-500 animate-pulse" />;
    }
  };

  const getStatusText = () => {
    switch (connectionStatus) {
      case 'connected':
        return 'Conectado';
      case 'disconnected':
        return 'Desconectado';
      case 'error':
        return 'Error de conexión';
      default:
        return 'Verificando...';
    }
  };

  const getStatusColor = () => {
    switch (connectionStatus) {
      case 'connected':
        return 'text-green-600 dark:text-green-400';
      case 'disconnected':
        return 'text-red-600 dark:text-red-400';
      case 'error':
        return 'text-yellow-600 dark:text-yellow-400';
      default:
        return 'text-gray-600 dark:text-gray-400';
    }
  };

  if (!user || !userData) {
    return null; // No mostrar si no hay usuario autenticado
  }

  // Render compacto para Header móvil
  if (compact) {
    return (
      <button
        onClick={checkConnection}
        disabled={connectionStatus === 'checking'}
        className={`flex items-center space-x-1.5 px-2.5 py-1 ${COLORS.background.secondary} hover:bg-gray-100 dark:hover:bg-gray-600 rounded-full border ${COLORS.border.primary} text-xs font-semibold cursor-pointer transition-colors active:scale-95 disabled:opacity-75`}
        title={`Verificar conexión. Última: ${lastCheck ? lastCheck.toLocaleTimeString() : 'N/A'}`}
      >
        {getStatusIcon()}
        <span className={`${getStatusColor()} text-[11px] font-bold`}>
          {connectionStatus === 'connected' ? 'Línea' : connectionStatus === 'disconnected' ? 'Offline' : getStatusText()}
        </span>
      </button>
    );
  }

  return (
    <div className={`${COLORS.background.secondary} rounded-lg p-3 border ${COLORS.border.primary}`}>
      <div className="flex items-center space-x-2">
        {getStatusIcon()}
        <div className="flex-1 min-w-0">
          <div className={`text-sm font-medium ${getStatusColor()} dark:text-white`}>
            {getStatusText()}
          </div>
          {lastCheck && (
            <div className="text-xs text-gray-500 dark:text-gray-300 truncate">
              Última verificación: {lastCheck.toLocaleTimeString()}
            </div>
          )}
        </div>
        <button
          onClick={checkConnection}
          disabled={connectionStatus === 'checking'}
          className="flex-shrink-0 p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-50 transition-colors"
          title="Verificar conexión"
        >
          <RefreshCw className={`h-3 w-3 text-gray-600 dark:text-gray-300 ${connectionStatus === 'checking' ? 'animate-spin' : ''}`} />
        </button>
      </div>
    </div>
  );
};
